import type {ReactElement} from 'react';
import React, {useContext} from 'react';
import type {Component, Editor} from 'grapesjs';
import {BottomBarItem} from '@Components/contextual-menu/components/bottom-bar-item/bottom-bar-item';
import {openEmailMakerMobileControlModal} from '@Components/modals/email-maker-mobile-control-modal/email-maker-mobile-control-modal';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {getResolvedActions} from '@EmailMakerReact/lib/email-maker-tile-meta-library';
import {TileComplexity} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import type {EmailMakerActionMeta, EmailMakerResolvedAction} from '@EmailMakerReact/lib/email-maker-tile-meta-library.types';
import type {EmailMakerImmutableStore} from '@EmailMakerReact/email-maker-react.types';
import {ICON_BUTTON_CONTROL_DESCRIPTORS} from '@EmailMakerReact/layout/email-maker-right-panel/controls/icon-button-control/icon-button-control';
import {MobileIconButtonAction} from './mobile-icon-button-action';
import styles from './action-strip.module.scss';

export function ActionStrip(): ReactElement | null {
  const {state, editor, store} = useContext(emailMakerContext);
  const selected = state.selectedComponent;

  const actions = selected && editor ? getResolvedActions(editor, selected, store) : [];
  if (actions.length === 0 || !editor) {
    return null;
  }

  return <>{actions.map((action) => renderAction(action, editor, store))}</>;
}

function renderAction(action: EmailMakerResolvedAction, editor: Editor, store: EmailMakerImmutableStore): ReactElement | null {
  if (action.actionMeta) {
    return renderActionMetaItem(action.flatIndex, action.target, action.actionMeta, action.key, editor, store);
  }
  return renderInlineAction(action);
}

function renderActionMetaItem(flatIndex: number, target: Component, actionMeta: EmailMakerActionMeta, key: string, editor: Editor, store: EmailMakerImmutableStore): ReactElement {
  const handleClick = (): void => {
    if (actionMeta.onActivate) {
      actionMeta.onActivate({editor, target, store});
      return;
    }
    openEmailMakerMobileControlModal({flatIndex, isFullHeight: actionMeta.complexity === TileComplexity.COMPLEX});
  };

  return (
    <BottomBarItem
      key={key}
      id={`email-maker-action-${key}`}
      icon={actionMeta.icon}
      text={window.i18next.t(actionMeta.labelKey)}
      textClasses={styles.label}
      onClick={handleClick}
    />
  );
}

/** Instant/toggle icon-button action as a captioned bottom-bar item. */
function renderInlineAction(action: EmailMakerResolvedAction): ReactElement | null {
  if (!action.control) {
    return null;
  }
  const descriptor = ICON_BUTTON_CONTROL_DESCRIPTORS.get(action.control);
  if (descriptor) {
    return <MobileIconButtonAction key={action.key} component={action.target} descriptor={descriptor} />;
  }
  return React.createElement(action.control, {component: action.target, key: action.key});
}
